import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Small shared pieces for the hand-rolled SVG charts: a measured container,
 * a pointer-following tooltip, and a count-up hook.
 *
 * The charts are SVG rather than a charting library because they all need to
 * be click-through to the activity inspector and to follow the themed CSS
 * tokens — both of which fight a library's own rendering model.
 */

/* ── Responsive measurement ────────────────────────────────────────────── */

export function useMeasure<T extends HTMLElement>(): [
  React.RefObject<T>,
  { width: number; height: number },
] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) =>
        Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1
          ? prev
          : { width, height }
      );
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
}

/* ── Tooltip ───────────────────────────────────────────────────────────── */

interface TooltipState {
  content: ReactNode;
  x: number;
  y: number;
}

interface TooltipApi {
  show: (content: ReactNode, event: { clientX: number; clientY: number }) => void;
  hide: () => void;
}

const TooltipContext = createContext<TooltipApi | null>(null);

export function TooltipProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TooltipState | null>(null);

  const show = useCallback<TooltipApi['show']>((content, event) => {
    setState({ content, x: event.clientX, y: event.clientY });
  }, []);

  const hide = useCallback(() => setState(null), []);

  // A scroll while the pointer is stationary leaves the tooltip stranded.
  useEffect(() => {
    if (!state) return;
    const onScroll = () => setState(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [state]);

  return (
    <TooltipContext.Provider value={{ show, hide }}>
      {children}
      {state && (
        <div
          className="chart-tooltip"
          style={{
            // Flip to the left of the cursor near the right edge.
            left: state.x + 300 > window.innerWidth ? undefined : state.x + 14,
            right:
              state.x + 300 > window.innerWidth
                ? window.innerWidth - state.x + 14
                : undefined,
            top: Math.min(state.y + 14, window.innerHeight - 120),
          }}
        >
          {state.content}
        </div>
      )}
    </TooltipContext.Provider>
  );
}

export function useTooltip(): TooltipApi {
  const context = useContext(TooltipContext);
  // Charts render fine without a provider; the tooltip simply does nothing.
  return context ?? { show: () => {}, hide: () => {} };
}

/* ── Count-up ──────────────────────────────────────────────────────────── */

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Eases a number up from zero when it changes. */
export function useCountUp(target: number, durationMs = 750): number {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? target : 0));
  const frameRef = useRef<number>();
  const fromRef = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion() || !Number.isFinite(target)) {
      setValue(target);
      return;
    }

    const from = fromRef.current;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      // easeOutCubic — fast arrival, gentle settle
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = from + (target - from) * eased;
      setValue(next);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      fromRef.current = value;
    };
    // `value` is deliberately excluded — including it restarts the animation
    // on every frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return value;
}

/* ── Formatting ────────────────────────────────────────────────────────── */

export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}bn`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return value.toFixed(0);
}

export function formatCurrency(value: number): string {
  return `£${formatCompact(value)}`;
}

export function formatDate(date: Date | null): string {
  if (!date) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDays(value: number): string {
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded}d`;
}

/** Nice round axis ticks covering [0, max]. */
export function niceTicks(max: number, count = 5): number[] {
  if (max <= 0) return [0];
  const rawStep = max / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalised = rawStep / magnitude;
  const step =
    (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude;

  const ticks: number[] = [];
  for (let tick = 0; tick <= max + step * 0.5; tick += step) ticks.push(tick);
  return ticks;
}

/* ── Empty state ───────────────────────────────────────────────────────── */

export function ChartEmpty({ message }: { message: string }) {
  return (
    <div className="flex h-full min-h-[180px] items-center justify-center px-6 text-center">
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        {message}
      </p>
    </div>
  );
}
